<template>
  <div class="place-selection-select-tab h-100">
    <div class="left">
      <PlacesControls
        title="Places"
        compact
        :searchTerm="searchTerm"
        :sourceFilter="sourceFilter"
        :sourceOptions="sourceOptions"
        :activeFilters="activeFilters"
        :activeFilterLabels="activeFilterLabels"
        :availableFilters="availableFilters"
        :filterSuggestions="filterSuggestions"
        :filterLoading="filterLoading"
        :filterErrors="filterErrors"
        @search-change="onSearchChange"
        @source-change="onSourceChange"
        @filter-change="onFilterChange"
        @filter-clear="onFilterClear"
        @clear-all-filters="onClearAllFilters"
        @filter-suggest="onFilterSuggest"
      />

      <div class="results" style="position: relative;">
        <div
          v-if="locationsLoading"
          class="text-center"
          style="margin-top: -15px;color: #6e84a3; position:absolute; left:0; right:0; padding-top: 50px; background-color: rgba(255,255,255,0.7); z-index:10; height: 100%;"
        >
          <WorldLoader />
        </div>
        <b-list-group class="loc-group">
          <b-list-group-item
            v-for="loc in locations"
            :key="loc.near_poi_id"
            class="loc-item d-flex justify-content-between align-items-center"
          >
            <div>
              <div class="loc-header">
                {{ locationName(loc) }}
                <b-icon icon="zoom-in" scale="0.8" style="cursor:pointer" @click="zoomTo(loc)" />
              </div>
              <div class="loc-content">
                {{ loc.address }}
              </div>
              <div class="loc-footer">
                {{ loc.cat1 }} / {{ loc.cat2 }}
              </div>
            </div>
            <div>
              <b-form-checkbox
                v-model="loc.checked"
                :name="`checkbox-${loc.near_poi_id}`"
                :disabled="selectAllMode"
                @change="toggleLocation(loc)"
              />
            </div>
          </b-list-group-item>
        </b-list-group>
      </div>

      <b-container>
      <b-row v-if="pagination" class="mt-2" style="border-top: 1px solid #ddd; padding-top: 8px;">
        <b-col>
          <b-button-group size="sm">
            <b-button
              variant="outline-secondary"
              :disabled="prevDisabled"
              @click="onPrevPage"
            >
              Prev
            </b-button>
            <b-button
              variant="outline-secondary"
              :disabled="nextDisabled"
              @click="onNextPage"
            >
              Next
            </b-button>
          </b-button-group>
        </b-col>
        <b-col v-if="pagination.records" class="mr-2 text-right pagination-info">
          Displaying {{ paginationInfo.from }}-{{ paginationInfo.to }} of {{ pagination.records | formatNumberWithUnits('') }}
        </b-col>
      </b-row>
      <b-row class="select-all-strip align-items-center">
        <b-col>
          <b-link v-if="showSelectAllLink" @click="onToggleSelectAll(true)">
            {{ selectAllLabel }}
          </b-link>
          <b-link v-if="selectAllMode" @click="onToggleSelectAll(false)">
            Clear select all
          </b-link>
        </b-col>
        <b-col v-if="showProgressBar">
          <b-progress :value="collectPercent" :max="100" />
        </b-col>
      </b-row>
      </b-container>

      <div class="d-flex justify-content-center">
        <b-button
          variant="primary"
          :disabled="addSelectedDisabled"
          @click="addSelected"
        >
          {{ addSelectedLabel }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import ChartUtilMixin from '@/services/charts/chartUtil.js';
import { GetMixin } from 'pinnacle-lib';
import WorldLoader from '@/components/home/new/WorldLoader.vue';
import PlacesControls from '@/components/places/PlacesControls.vue';
import jobService from '@/services/dataExplorer/jobService.js';
import Places_Service from '@/services/dataExplorer/placesService.js';
import PlacesFilterControlsMixin from '@/services/dataExplorer/placesFilterControlsMixin.js';
import {
  shouldOfferSelectAll,
  formatSelectAllLabel,
  planOffsetPages,
  computeCollectionProgress,
  shouldShowProgressBar,
  COLLECTION_PAGE_SIZE,
  PAGE_TIMEOUT_MS,
} from '@/components/places/utils/placesSelectAllAdapter';
import {
  computeOffsetRange,
  computeCursorRange,
} from '@/components/places/utils/placesAdapter';

// At/above this map zoom, the tab auto-resolves polygon geometries for the
// current page's places that fall within the map viewport and don't already
// have a shape (draw-only; no selection side effects).
const AUTO_GEOMETRY_ZOOM_THRESHOLD = 12;

// Pure containment test: is [lon, lat] inside the axis-aligned viewport bounds
// { west, south, east, north }? Datasets here are US-centric (the tab defaults
// country to 'USA'), so we don't handle antimeridian-crossing bounds — a check
// that would matter only for viewports straddling ±180° longitude.
function pointInBounds(lon, lat, bounds) {
  if (!bounds || !Number.isFinite(lon) || !Number.isFinite(lat)) return false;
  return (
    lon >= bounds.west
    && lon <= bounds.east
    && lat >= bounds.south
    && lat <= bounds.north
  );
}

export default {
  name: 'PlaceSelectionSelectTab',
  mixins: [GetMixin, ChartUtilMixin, jobService, Places_Service, PlacesFilterControlsMixin],
  components: {
    WorldLoader,
    PlacesControls,
  },
  props: {
    mapClickEvent: {
      type: Object,
      default: null,
    },
    geocodedCountryCommand: {
      type: Object,
      default: null,
    },
    // Token-bearing viewport command from the shared map (via the modal):
    // { bounds:{west,south,east,north}, zoom, token }. Watched to auto-fill
    // in-view place geometries once zoomed in.
    viewportCommand: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      // The Select tab debounces search at 500 ms (Req 1.3); overrides the
      // mixin default of 350 ms.
      searchDebounceMs: 500,
      // Tab-specific result/selection state.
      locations: [],
      locationsGeoJSON: null,
      locationsLoadFailed: false,
      pagination: {
        page: 1,
        page_size: 100,
        records: 0,
        pages: 0,
      },
      // Cursor-based pagination state for the Custom source. The Custom
      // downstream (places/search/pois) pages via a `search_after` cursor, not
      // numbered offsets, so the numbered `pagination` object above is used for
      // the Azira offset path only.
      // - cursorStack: cursors used to reach the CURRENT window. Empty stack =>
      //   first window (no search_after); the top is the current window cursor.
      // - hasMore: whether a distinct next window exists (response has_more).
      // - nextCursor: the search_after for the current window, used to advance.
      // - pageSize: fixed server-side window size (100) for the Custom source.
      cursorStack: [],
      hasMore: false,
      nextCursor: null,
      pageSize: 100,
      selectedIndexes: [],
      locationsLoading: false,
      // Select_All feature state (transient; reset on query/source/company/page
      // change). selectAllMode toggles the full-match-set selection model;
      // collecting/collectPercent drive the Progress_Bar during Match_Collection.
      selectAllMode: false,
      collecting: false,
      collectPercent: 0,
      // Distinguishes the initial load (empty the list on failure, Req 3.7)
      // from a reload (retain previously displayed results, Req 1.6/8.9). Flips
      // to true once the first load attempt resolves.
      hasLoadedOnce: false,
      // Monotonic token guarding the async zoom-to-polygon flow. Captured when a
      // zoom starts; a resolved geometry is only applied when the token still
      // matches, so a slow response can't render onto a newer result set (after
      // a page/search/other-zoom change). loadResults bumps it on every rebuild.
      zoomRequestToken: 0,
      // near_poi_ids already submitted to the auto-geometry bulk resolver for
      // the CURRENT result set. Prevents re-requesting the same places on every
      // small pan/zoom (including places that resolved to no shape). Cleared
      // whenever loadResults rebuilds the list.
      autoGeometryRequested: new Set(),
      // Concurrency guard so overlapping viewport changes don't fire multiple
      // simultaneous bulk-geometry resolutions.
      autoGeometryResolving: false,
    };
  },
  computed: {
    locationsSelected() {
      return this.locations.filter((loc) => loc.checked);
    },
    // Req 1.1/1.2/1.3/1.6/1.7 — gate the Select_All_Link via the reused pure
    // helper: offered only when Match_Total is a positive integer below the
    // 10,000 threshold, the tab is not loading, and Select_All_Mode is off.
    showSelectAllLink() {
      return shouldOfferSelectAll(this.pagination.records, {
        loading: this.locationsLoading,
        selectAllMode: this.selectAllMode,
      });
    },
    // Req 1.4 — "Select all X places" with X comma-grouped.
    selectAllLabel() {
      return formatSelectAllLabel(this.pagination.records);
    },
    // Req 2.4/4.4 — the count source switches with the selection model:
    // Match_Total in Select_All_Mode, else the checked-item count.
    addSelectedCount() {
      return this.selectAllMode ? this.pagination.records : this.selectedIndexes.length;
    },
    // Req 2.4 — Add_Selected_Button label with the mode-dependent count.
    addSelectedLabel() {
      return `Add Selected Places (${this.addSelectedCount})`;
    },
    // Req 8.1 — busy always disables; otherwise mode picks the empty rule
    // (no matches in Select_All_Mode, no checked items otherwise).
    addSelectedDisabled() {
      if (this.locationsLoading) return true;
      return this.selectAllMode
        ? this.pagination.records <= 0
        : this.selectedIndexes.length === 0;
    },
    // Req 7.1/7.2 — show the Progress_Bar only during a multi-page collection.
    showProgressBar() {
      return shouldShowProgressBar(this.collecting, this.pagination.records, COLLECTION_PAGE_SIZE);
    },
    // The Custom source pages by cursor; every other source pages by offset.
    paginationMode() {
      return this.sourceFilter === 'Custom' ? 'cursor' : 'offset';
    },
    // Displayed range for the "Displaying X-Y of N" line. Mode-aware so it works
    // for BOTH sources: Azira uses the numbered offset window; Custom (which has
    // no `from`/`page` in its response) derives the window from the cursor-stack
    // depth. Reuses the same pure helpers as the Places page.
    paginationInfo() {
      const displayedCount = this.locations.length;
      const range = this.paginationMode === 'cursor'
        ? computeCursorRange(
          this.cursorStack.length,
          this.pageSize,
          this.pagination.records,
          displayedCount,
        )
        : computeOffsetRange(
          Number(this.pagination.page) || 1,
          Number(this.pagination.page_size) || 0,
          this.pagination.records,
          displayedCount,
        );
      if (!range.valid) return { from: 0, to: 0 };
      return { from: range.start, to: range.end };
    },
    // Prev/Next gating. Disabled while loading or during a select-all
    // collection. Cursor mode: Prev is available once we've advanced past the
    // first window (stack non-empty), Next while the response reports has_more.
    // Offset mode: Prev on any page after the first; Next until the displayed
    // range reaches the total.
    prevDisabled() {
      if (this.locationsLoading || this.selectAllMode) return true;
      if (this.paginationMode === 'cursor') return this.cursorStack.length === 0;
      return (Number(this.pagination.page) || 1) <= 1;
    },
    nextDisabled() {
      if (this.locationsLoading || this.selectAllMode) return true;
      if (this.paginationMode === 'cursor') return !this.hasMore;
      const records = Number(this.pagination.records) || 0;
      return records === 0 || this.paginationInfo.to >= records;
    },
  },
  watch: {
    locations: {
      deep: true,
      handler(newVal) {
        this.selectedIndexes = newVal.filter((loc) => loc.checked).map((loc) => loc.near_poi_id);
      },
    },
    mapClickEvent: {
      deep: true,
      handler(event) {
        if (!event || !event.placematrixId) return;
        this.onMapPointClicked(event.placematrixId);
      },
    },
    // Req 3.1/3.4 — a geocoder search that yields a country populates the
    // Country filter. Delegate resolve/apply to the mixin's
    // applyGeocodedCountry; the component only wires the watcher.
    geocodedCountryCommand: {
      deep: true,
      handler(cmd) {
        if (!cmd || !cmd.alpha3) return;
        this.applyGeocodedCountry(cmd.alpha3);
      },
    },
    // Auto-fill polygon geometries for in-view places once the map is zoomed
    // in past the threshold. Draw-only: never touches selection.
    viewportCommand: {
      deep: true,
      handler(cmd) {
        if (!cmd || !cmd.bounds || typeof cmd.zoom !== 'number') return;
        this.autoFillInViewGeometries(cmd.bounds, cmd.zoom);
      },
    },
    // Reset seams (Req 5.1–5.4): any change to the observable query/source/
    // company/page state exits Select_All_Mode via the idempotent, busy-guarded
    // resetSelectAllMode(). Decoupled from the mixin's handler names.
    searchTerm() {
      this.resetSelectAllMode(); // Req 5.1
    },
    sourceFilter() {
      this.resetSelectAllMode(); // Req 5.2
    },
    selectedCompanyId() {
      this.resetSelectAllMode(); // Req 5.3
      this.resetCursor(); // Custom paging restarts from the first window
    },
    'pagination.page': function paginationPageWatcher() {
      this.resetSelectAllMode(); // Req 5.4
    },
    activeFilters: {
      deep: true,
      handler() {
        this.resetSelectAllMode(); // Req 5.1 (filters)
      },
    },
    // Req 2.8 — if Match_Total drops to 0 while Select_All_Mode is active, exit
    // the mode and clear the item selection.
    'pagination.records': function paginationRecordsWatcher(records) {
      if (this.selectAllMode && records === 0) {
        this.selectAllMode = false;
        this.resetSelection();
      }
    },
  },
  async created() {
    // Default to the Source the user last selected (persisted preference).
    this.sourceFilter = this.getPreferredSource();
    await this.applyDefaultCountryFilter();
    this.loadResults();
  },
  methods: {
    async addSelected() {
      // Req 8.2 — re-entrancy guard: a second activation while a handoff is in
      // flight short-circuits (reuses the single locationsLoading busy flag).
      if (this.locationsLoading) return;

      // Select_All_Mode delegates to the full-match-set handoff. This branch
      // must come BEFORE the geojson guard below, since select-all does not
      // rely on the current page's locationsGeoJSON (Req 6.1).
      if (this.selectAllMode) return this.addSelectedAll();

      if (!this.locationsGeoJSON || !Array.isArray(this.locationsGeoJSON.features)) return;

      const selected = this.locationsSelected;
      if (selected.length === 0) return;

      this.locationsLoading = true;
      try {
        // Look up the real polygon shape for every selected place before
        // committing it to the review list. Each place first passes through the
        // Local_Cache_Shortcut (cachedPolygonFor); only cache-miss places are
        // resolved, and they go through a SINGLE batched resolveBulkGeometry
        // call. A failed/absent geometry falls back to null so the place is
        // still added (as a point-only place).
        const isCustomSource = this.sourceFilter === 'Custom';

        // 1) Local_Cache_Shortcut first for every selected item (Req 13.1).
        const cached = new Map(); // near_poi_id -> geometry
        const misses = [];
        selected.forEach((loc) => {
          const hit = this.cachedPolygonFor(loc);
          if (hit) cached.set(loc.near_poi_id, hit);
          else misses.push(loc);
        });

        // 2) One batched resolution for the cache-miss items only (Req 13.2,
        // 13.3). A single call assumes one country; the Select tab's existing
        // behavior effectively defaults to 'USA'.
        let results = new Map();
        if (misses.length) {
          ({ results } = await this.resolveBulkGeometry(
            misses.map((l) => ({ near_poi_id: l.near_poi_id, lat: l.lat, lon: l.lon })),
            {
              custom: isCustomSource,
              companyId: this.selectedCompanyId,
              country: 'USA',
            },
          ));
        }

        const toAdd = selected.map((loc) => ({
          _source: 'library',
          // Custom records have no `unique_name` (their name lives in
          // poi_list_name/name_en), so deriving the name only from
          // unique_name left Custom places nameless -> "Unnamed"/"Unknown"
          // downstream. Reuse locationName (same helper the list renders)
          // and fall back to unique_name for Azira records.
          name: this.locationName(loc) || loc.unique_name,
          // Cache hit wins, then the batched result, then null (point-only)
          // (Req 13.4). No per-miss follow-up.
          geometry: cached.get(loc.near_poi_id) || results.get(loc.near_poi_id) || null,
          near_poi_id: loc.near_poi_id,
          placematrix_id: loc.placematrix_id,
          // Flag Custom-library selections so the Review screen groups them
          // under "Custom" (their placematrix_id does not always carry the
          // `_C_` token the classifier otherwise relies on).
          custom: isCustomSource,
          // Carry Category/Brand from the search record so the Review
          // screen can show the same meta line as Places to Save. Mirrors
          // normalizePlaceRecord: category prefers cat2 then cat1; brand
          // prefers poi_list_name then brand.
          category: loc.cat2 || loc.cat1 || null,
          brand: loc.poi_list_name || loc.brand || null,
          // Carry any existing tags from the search record so the Review
          // screen can display them on the library row.
          labels: Array.isArray(loc.labels) ? loc.labels : [],
        }));

        const beforeCount = this.$sessionStore.state.placeSelection.selectedPlaces.length;
        this.$sessionStore.commit('addSelectedPlaces', toAdd);
        const afterCount = this.$sessionStore.state.placeSelection.selectedPlaces.length;
        // The store silently caps additions at the per-flow Max_Places. When
        // fewer places landed than we tried to add, tell the user why instead
        // of dropping them with no feedback.
        this.notifyIfPlaceLimitHit(toAdd.length, afterCount - beforeCount);
        this.resetAfterAddSelected();
        this.emitExploreData();
      } catch (e) {
        this.$bvToast.toast("Couldn't load place shapes.", {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.locationsLoading = false;
      }
    },
    // Select_All_Mode handoff: collect the full match set (offset-paged, both
    // sources), resolve geometry per place, and route it into the same
    // addSelectedPlaces commit + reset + emit path as the per-item flow
    // (Req 6, 7, 8, 9). locationsLoading is the single busy guard; selectAllMode
    // is only cleared on a successful handoff so it survives a failure for
    // retry (Req 9.5).
    async addSelectedAll() {
      const total = this.pagination.records;
      this.locationsLoading = true; // Req 6.2, 8.4 — Add_Selected_Busy
      this.collecting = true; // Req 7 — Progress_Bar visibility gate
      this.collectPercent = 0; // Req 7.3
      try {
        const records = await this.collectAllMatches(total, COLLECTION_PAGE_SIZE);
        this.collectPercent = 100; // Req 7.5 — 100% before the handoff

        // Resolve the polygon shape for every collected place. Each place first
        // passes through the Local_Cache_Shortcut (cachedPolygonFor); only
        // cache-miss places are resolved, and they go through a SINGLE batched
        // resolveBulkGeometry call (Req 14.2, 14.3). A failed/absent geometry
        // falls back to null so the place is still added point-only (Req 14.5).
        const isCustomSource = this.sourceFilter === 'Custom';

        // 1) Local_Cache_Shortcut first for every collected place (Req 14.2).
        const cached = new Map(); // near_poi_id -> geometry
        const misses = [];
        records.forEach((loc) => {
          const hit = this.cachedPolygonFor(loc);
          if (hit) cached.set(loc.near_poi_id, hit);
          else misses.push(loc);
        });

        // 2) One batched resolution for the cache-miss places only (Req 14.3,
        // 14.4). Defensive: if resolveBulkGeometry rejects OR returns an
        // unsuccessful/non-Map result, treat all misses as point-only (null) —
        // no per-place fallback, and never abort the handoff (Req 14.6).
        let results = new Map();
        if (misses.length) {
          try {
            const resolved = await this.resolveBulkGeometry(
              misses.map((l) => ({ near_poi_id: l.near_poi_id, lat: l.lat, lon: l.lon })),
              {
                custom: isCustomSource,
                companyId: this.selectedCompanyId,
                country: 'USA',
              },
            );
            if (resolved && resolved.ok && resolved.results instanceof Map) {
              results = resolved.results;
            }
          } catch (e) {
            results = new Map();
          }
        }

        // One entry per collected place, in collection order (Req 14.7). Cache
        // hit wins, then the batched result, then null (point-only) (Req 14.5).
        const toAdd = records.map((loc) => ({
          _source: 'library',
          // Custom records have no `unique_name` (their name lives in
          // poi_list_name/name_en), so deriving the name only from unique_name
          // left Custom places nameless. Reuse locationName (same helper the
          // list renders) and fall back to unique_name for Azira records.
          name: this.locationName(loc) || loc.unique_name,
          geometry: cached.get(loc.near_poi_id) || results.get(loc.near_poi_id) || null,
          near_poi_id: loc.near_poi_id,
          placematrix_id: loc.placematrix_id,
          // Flag Custom-library selections so the Review screen groups them
          // under "Custom" (their placematrix_id does not always carry the
          // `_C_` token the classifier otherwise relies on).
          custom: isCustomSource,
          // Carry Category/Brand from the search record so the Review
          // screen can show the same meta line as Places to Save. Mirrors
          // normalizePlaceRecord: category prefers cat2 then cat1; brand
          // prefers poi_list_name then brand.
          category: loc.cat2 || loc.cat1 || null,
          brand: loc.poi_list_name || loc.brand || null,
          // Carry any existing tags from the search record so the Review
          // screen can display them on the library row.
          labels: Array.isArray(loc.labels) ? loc.labels : [],
        }));

        // Same commit + reset + emit path as per-item addSelected (Req 14.7).
        const beforeCount = this.$sessionStore.state.placeSelection.selectedPlaces.length;
        this.$sessionStore.commit('addSelectedPlaces', toAdd);
        const afterCount = this.$sessionStore.state.placeSelection.selectedPlaces.length;
        // Surface the per-flow Max_Places cap when the store truncated the add.
        this.notifyIfPlaceLimitHit(toAdd.length, afterCount - beforeCount);
        this.selectAllMode = false; // exit mode after a successful handoff
        this.resetAfterAddSelected();
        this.emitExploreData();
      } catch (e) {
        if (e && e.collectionFailed) {
          // Req 9.1, 9.3, 9.5 — discard the partial set (never committed), keep
          // Select_All_Mode active (checkboxes stay disabled) for retry, and
          // surface the failure.
          this.$bvToast.toast('Select all collection failed. Please try again.', {
            title: 'Select all',
            variant: 'danger',
            solid: true,
          });
        } else {
          this.$bvToast.toast("Couldn't add selected places.", {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      } finally {
        this.locationsLoading = false; // Req 6.8, 8.5, 9.4
        this.collecting = false; // Req 7.6
        this.collectPercent = 0;
      }
    },
    // Local_Cache_Shortcut (Req 13.1): pure lookup of a place's polygon in the
    // already-loaded locationsGeoJSON. Returns the geometry of the FIRST feature
    // (in locationsGeoJSON order) whose `placematrix_id` matches loc's and whose
    // geometry type is Polygon/MultiPolygon; returns null otherwise. No side
    // effects, no network — safe to call per item before batching cache misses.
    cachedPolygonFor(loc) {
      const features = this.locationsGeoJSON && Array.isArray(this.locationsGeoJSON.features)
        ? this.locationsGeoJSON.features
        : null;
      if (!features) return null;
      const existing = features.find(
        (f) => f.properties.placematrix_id === loc.placematrix_id
          && f.geometry
          && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'),
      );
      return existing ? existing.geometry : null;
    },
    // Resolve the polygon geometry for a location: prefer a Polygon/MultiPolygon
    // feature already present in locationsGeoJSON (via cachedPolygonFor),
    // otherwise resolve it through the SAME batched path the add-selected flow
    // uses (resolveBulkGeometry with a single-element array). This unifies zoom
    // and add-selected on one endpoint (/v2/places-bulk-detail) and one geometry
    // precedence (Polygon/MultiPolygon -> Point fallback -> null). A shorter
    // interactive timeout is used so a single zoom click never hangs for the
    // full bulk default. resolveBulkGeometry never throws (returns a Map), but
    // the try/catch is kept as a defensive belt for callers.
    async resolvePolygonGeometry(loc) {
      const cached = this.cachedPolygonFor(loc);
      if (cached) return cached;

      try {
        const { results } = await this.resolveBulkGeometry(
          [{ near_poi_id: loc.near_poi_id, lat: loc.lat, lon: loc.lon }],
          {
            custom: this.sourceFilter === 'Custom',
            companyId: this.selectedCompanyId,
            country: loc.country || 'USA',
            // Interactive single-zoom timeout (vs the 30s bulk default) so the
            // click doesn't hang; matches the prior places-detail 15s intent.
            timeout: 15000,
          },
        );
        return (results && results.get(loc.near_poi_id)) || null;
      } catch (e) {
        return null;
      }
    },
    resetAfterAddSelected() {
      this.selectedIndexes = [];
      this.locations.forEach((loc) => {
        loc.checked = false;
      });
    },
    // Warn when the per-flow Max_Places cap prevented some (or all) of an add.
    // `attempted` is how many places the user tried to add; `added` is how many
    // the store actually accepted after enforcing its cap. A positive shortfall
    // means the limit truncated the add. We emit an event (instead of a toast)
    // so the modal can surface it as an INLINE footer alert: the modal's
    // z-index sits above the BootstrapVue toaster, so toasts fired while it is
    // open are painted behind it and never seen. No-op when nothing was dropped
    // (added >= attempted) or there is no cap.
    notifyIfPlaceLimitHit(attempted, added) {
      const dropped = Number(attempted) - Number(added);
      if (!(dropped > 0)) return;
      const max = Number(this.$sessionStore.state.placeSelection.maxPlaces) || 0;
      const message = max === 1
        ? 'This flow allows a maximum of 1 place.'
        : `This flow allows a maximum of ${max} place(s); extra selections were not added.`;
      this.$emit('place-limit-hit', message);
    },
    locationName(loc) {
      if (loc.name_en && loc.poi_list_name && loc.name_en.includes(loc.poi_list_name)) {
        return loc.name_en;
      }
      return `${loc.poi_list_name || ''} - ${loc.name_en || ''}`;
    },
    toggleLocation() {
      this.emitExploreData();
    },
    // Host hook for PlacesFilterControlsMixin: fetch + render results for the
    // current source/filter/page state. Custom source uses
    // getPlacesSearchPois; Azira source uses getPlacesFilteredPolygons. Both
    // return { records, pagination }. Results feed the checkbox list and the
    // GeoJSON emitted to the shared map.
    async loadResults() {
      this.locationsLoading = true;
      try {
        let resp;
        if (this.sourceFilter === 'Custom') {
          // Custom with no company context: empty list, issue no request
          // (Req 3.5).
          if (!this.selectedCompanyId) {
            this.locations = [];
            this.pagination.records = 0;
            return;
          }
          // Custom source retrieval (Req 3.4). The backend pages via a
          // `search_after` cursor and ignores page/page_size, so send the
          // current window's cursor (top of the stack, or null for the first
          // window) rather than an offset.
          const currentCursor = this.cursorStack.length
            ? this.cursorStack[this.cursorStack.length - 1]
            : null;
          resp = await this.getPlacesSearchPois({
            companyId: this.selectedCompanyId,
            custom: true,
            filters: this.activeFilters,
            search_after: currentCursor,
          });
        } else {
          // Azira source retrieval (Req 3.3).
          this.setSearchParams(this.searchTerm);
          resp = await this.getPlacesFilteredPolygons({
            page: this.pagination.page,
            page_size: this.pagination.page_size,
            filters: this.activeFilters,
          });
        }

        const records = resp.records || [];
        const respPagination = resp.pagination || {};
        if (this.sourceFilter === 'Custom') {
          // Cursor contract: keep the numbered `pagination` object only for the
          // total (records) used by the "Displaying" line; drive Prev/Next off
          // hasMore/nextCursor. The response omits `from`/`page`/`pages`.
          this.hasMore = !!respPagination.has_more;
          this.nextCursor = respPagination.search_after || null;
          this.pagination = {
            ...this.pagination,
            records: Number(respPagination.records) || 0,
          };
        } else {
          // Azira offset path: merge (not replace) so numbered-paging fields
          // survive and derive `pages` when the response omits it.
          const mergedPageSize = Number(respPagination.page_size)
            || this.pagination.page_size
            || 100;
          const mergedRecords = Number.isFinite(Number(respPagination.records))
            ? Number(respPagination.records)
            : this.pagination.records;
          this.pagination = {
            ...this.pagination,
            ...respPagination,
            page_size: mergedPageSize,
            records: mergedRecords,
            page: Number(respPagination.page) || this.pagination.page || 1,
            pages: Number(respPagination.pages)
              || Math.max(Math.ceil((mergedRecords || 0) / mergedPageSize), 1),
          };
        }
        // Preserve checked=false default on every list item.
        this.locations = records.map((record) => ({ ...record, checked: false }));
        // Rebuilding the result set invalidates any in-flight zoom: a geometry
        // request tied to the previous list must not apply to this new one.
        this.zoomRequestToken += 1;
        // New result set => the auto-geometry request history no longer applies.
        this.autoGeometryRequested = new Set();
        this.buildGeoJSON(records);
        this.locationsLoadFailed = false;
        this.emitExploreData();
      } catch (e) {
        this.locationsLoadFailed = true;
        // On the INITIAL load, empty the list (Req 3.7). On a RELOAD, retain
        // previously displayed results (Req 1.6, 8.9).
        if (!this.hasLoadedOnce) {
          this.locations = [];
        }
        this.$bvToast.toast("Couldn't reload places.", {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.hasLoadedOnce = true;
        this.locationsLoading = false;
      }
    },
    // Host hook for PlacesFilterControlsMixin: clear the current selection.
    resetSelection() {
      this.locations.forEach((loc) => {
        loc.checked = false;
      });
      this.selectedIndexes = [];
    },
    // Enter/exit Select_All_Mode (Req 2, 4).
    onToggleSelectAll(enter) {
      if (enter) {
        const total = this.pagination.records;
        // Req 2.6 — Match_Total 0: do not enter, and surface no error (an empty
        // result set is not a failure). Req 2.7 — an indeterminate Match_Total
        // (null/undefined/NaN/non-integer/negative) blocks entry and surfaces
        // a danger toast.
        if (!Number.isInteger(total) || total <= 0) {
          if (total !== 0) {
            this.$bvToast.toast("Couldn't select all places (match count unavailable).", {
              title: 'Select all',
              variant: 'danger',
              solid: true,
            });
          }
          return;
        }
        this.selectAllMode = true; // Req 2.1
        this.resetSelection(); // Req 2.3 — Item_Selection_Count -> 0
      } else {
        if (this.locationsLoading) return; // Req 4.6 — cannot clear mid-collection
        this.selectAllMode = false; // Req 4.1
        this.resetSelection(); // Req 4.3 — Item_Selection_Count -> 0
      }
    },
    // Reset Select_All_Mode from the watcher reset seams (Req 5). Idempotent and
    // inert while a Match_Collection is in flight (Req 5.7).
    resetSelectAllMode() {
      if (this.locationsLoading) return; // Req 5.7 — no reset mid-collection
      if (!this.selectAllMode) return;
      this.selectAllMode = false;
      this.resetSelection(); // Req 5.5 — Item_Selection_Count -> 0
    },
    // Issue ONE offset page for the active source with a 30s timeout guard,
    // normalizing any rejection/timeout into a thrown { collectionFailed: true }
    // (Req 9.1, 9.2). This is the only source-aware seam of Match_Collection:
    // Custom uses getPlacesSearchPois (with companyId + custom); Azira uses
    // getPlacesFilteredPolygons. Both are offset-paged by page/page_size.
    async fetchCollectionPage(page, pageSize) {
      const request = this.sourceFilter === 'Custom'
        ? this.getPlacesSearchPois({ // Req 6.5 — Custom, offset-paged
          page,
          page_size: pageSize,
          companyId: this.selectedCompanyId,
          custom: true,
          filters: this.activeFilters,
        })
        : this.getPlacesFilteredPolygons({ // Req 6.4 — Azira, offset-paged
          page,
          page_size: pageSize,
          filters: this.activeFilters,
        });
      const timeout = new Promise((_, reject) => {
        setTimeout(
          () => reject({ collectionFailed: true, reason: 'timeout' }),
          PAGE_TIMEOUT_MS,
        );
      });
      try {
        return await Promise.race([request, timeout]);
      } catch (e) {
        // Normalize any rejection/timeout into the fail-safe abort signal
        // consumed by collectAllMatches/addSelectedAll (Req 9.1, 9.2).
        if (e && e.collectionFailed) throw e;
        throw { collectionFailed: true, cause: e };
      }
    },
    // Match_Collection engine: page through the full result set for the active
    // source and return every matching record in order. Both Azira and Custom
    // are offset-paged here, so a single deterministic plan drives the loop
    // (Req 6.3, 6.4, 6.5, 6.6). Progress is reported after each page via the
    // pure computeCollectionProgress helper (Req 7.4). selectAllMode is NEVER
    // mutated here, so addSelectedAll's catch keeps it active for retry on a
    // page failure (Req 8.3, 9.5). fetchCollectionPage throws
    // { collectionFailed: true } on any page failure/timeout, which propagates
    // out of this method unhandled to stop the run.
    async collectAllMatches(total, pageSize) {
      const collected = [];
      const pages = planOffsetPages(total, pageSize); // [1..k] for both sources
      for (const page of pages) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await this.fetchCollectionPage(page, pageSize);
        collected.push(...(resp.records || [])); // Req 6.6 — append this page
        // Req 7.4 — non-decreasing, clamped 0..100 progress.
        this.collectPercent = computeCollectionProgress(collected.length, total);
      }
      return collected; // Req 6.3 — collected.length >= total
    },
    // Reload results for the given offset page (Azira). Guards to 1..pages.
    goToPage(page) {
      if (this.locationsLoading) return;
      const maxPage = this.pagination && this.pagination.pages ? this.pagination.pages : Infinity;
      if (page < 1 || page > maxPage) return;
      this.pagination.page = page;
      this.loadResults();
    },
    // Clear cursor state so Custom paging restarts from the first window (no
    // search_after). Called on every query/source/company/filter change.
    resetCursor() {
      this.cursorStack = [];
      this.hasMore = false;
      this.nextCursor = null;
    },
    // The mixin owns onSearchChange/onSourceChange/onFilterChange/onFilterClear/
    // onClearAllFilters and resets pagination.page = 1 before reloading for a
    // NEW query. Since the Custom source pages by cursor (not offset), override
    // each here to clear the cursor stack first, then delegate to the shared
    // mixin method (mirrors PlacesPage). The mixin file stays unedited.
    onSearchChange(...args) {
      this.resetCursor();
      return PlacesFilterControlsMixin.methods.onSearchChange.apply(this, args);
    },
    onSourceChange(...args) {
      this.resetCursor();
      return PlacesFilterControlsMixin.methods.onSourceChange.apply(this, args);
    },
    onFilterChange(...args) {
      this.resetCursor();
      return PlacesFilterControlsMixin.methods.onFilterChange.apply(this, args);
    },
    onFilterClear(...args) {
      this.resetCursor();
      return PlacesFilterControlsMixin.methods.onFilterClear.apply(this, args);
    },
    onClearAllFilters(...args) {
      this.resetCursor();
      return PlacesFilterControlsMixin.methods.onClearAllFilters.apply(this, args);
    },
    // Prev: cursor mode pops the current window's cursor (new top, or null, is
    // the prior window); offset mode steps the page number down.
    onPrevPage() {
      if (this.prevDisabled) return;
      if (this.paginationMode === 'cursor') {
        this.cursorStack.pop();
        this.loadResults();
        return;
      }
      this.goToPage((Number(this.pagination.page) || 1) - 1);
    },
    // Next: cursor mode pushes the current window's nextCursor (becomes the new
    // top / current window cursor); offset mode steps the page number up.
    onNextPage() {
      if (this.nextDisabled) return;
      if (this.paginationMode === 'cursor') {
        this.cursorStack.push(this.nextCursor);
        this.loadResults();
        return;
      }
      this.goToPage((Number(this.pagination.page) || 1) + 1);
    },
    // Build `locationsGeoJSON` directly from source records (both Azira and
    // Custom responses carry lon/lat and, for polygon results, geometry). This
    // replaces the previous separate POST to /v2/USA/places-geojson.
    //
    // The resulting FeatureCollection holds two kinds of features, both keyed
    // by placematrix_id so zoomTo (reads a point feature's lon/lat) and
    // addSelected (reads a polygon feature's geometry) can look them up:
    //  - point features:   properties { placematrix_id, lon, lat, name, ... }
    //  - polygon features:  geometry (Polygon/MultiPolygon) + properties { placematrix_id, name }
    // Records with invalid coordinates contribute no point feature but may
    // still contribute a polygon feature when polygon geometry is present.
    buildGeoJSON(records) {
      const features = [];
      const list = Array.isArray(records) ? records : [];

      list.forEach((record) => {
        if (!record) return;

        const lon = Number(record.lon);
        const lat = Number(record.lat);
        const hasValidCoords = record.lon !== null
          && record.lon !== undefined
          && record.lat !== null
          && record.lat !== undefined
          && Number.isFinite(lon)
          && Number.isFinite(lat);

        if (hasValidCoords) {
          features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [lon, lat],
            },
            properties: {
              placematrix_id: record.placematrix_id,
              lon,
              lat,
              name: this.locationName(record),
            },
          });
        }

        const geometry = record.geometry;
        if (geometry && (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon')) {
          features.push({
            type: 'Feature',
            geometry,
            properties: {
              placematrix_id: record.placematrix_id,
              name: this.locationName(record),
            },
          });
        }
      });

      this.locationsGeoJSON = {
        type: 'FeatureCollection',
        features,
      };
    },
    // Derive the map contract (fixed by PlaceSelectionSharedMap) from
    // locationsGeoJSON: pointsGeoJSON (Point features with placematrix_id +
    // name), polygonsGeoJSON (polygon features), and selectedPlacematrixIds
    // (placematrix_id of every checked location).
    emitExploreData() {
      const source = this.locationsGeoJSON && Array.isArray(this.locationsGeoJSON.features)
        ? this.locationsGeoJSON.features
        : [];

      const pointsGeoJSON = {
        type: 'FeatureCollection',
        features: source.filter((feature) => feature.geometry && feature.geometry.type === 'Point'),
      };

      const polygonsGeoJSON = {
        type: 'FeatureCollection',
        features: source.filter(
          (feature) => feature.geometry
            && (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon'),
        ),
      };

      const selectedPlacematrixIds = this.locations
        .filter((loc) => loc.checked)
        .map((loc) => loc.placematrix_id);

      this.$emit('explore-data-changed', {
        pointsGeoJSON,
        polygonsGeoJSON,
        selectedPlacematrixIds,
      });
    },
    onMapPointClicked(placematrixId) {
      // Req 3.3 — while Select_All_Mode is active a map point click must not
      // toggle an item's checked flag or grow the item selection.
      if (this.selectAllMode) return;
      const loc = this.locations.find((item) => item.placematrix_id === placematrixId);
      if (!loc) return;
      loc.checked = !loc.checked;
      this.emitExploreData();
    },
    async zoomTo(loc) {
      if (!this.locationsGeoJSON || !Array.isArray(this.locationsGeoJSON.features)) return;
      // buildGeoJSON stores center coords on the POINT feature (properties
      // lon/lat + geometry.coordinates); polygon features share the same
      // placematrix_id but carry no center, so match the Point feature.
      const match = this.locationsGeoJSON.features.find(
        (feature) => feature.geometry
          && feature.geometry.type === 'Point'
          && feature.properties.placematrix_id === loc.placematrix_id,
      );
      if (!match) return;

      // Fly to the place first so the interaction feels immediate, then fetch
      // and render its polygon shape.
      this.$emit('explore-zoom-to', {
        center: [match.properties.lon, match.properties.lat],
        zoom: 19,
      });

      // Capture a staleness token AFTER the immediate zoom emit. If the result
      // set changes while the geometry request is in flight (page/search/other
      // zoom), the token advances and the resolved value is discarded so it
      // can't render onto — or toast about — a place no longer in the list.
      const token = ++this.zoomRequestToken;
      try {
        const geometry = await this.resolvePolygonGeometry(loc);
        if (token !== this.zoomRequestToken) return; // stale: silently discard
        // resolvePolygonGeometry returns a Polygon/MultiPolygon, a Point
        // fallback, or null. Only Polygon/MultiPolygon is drawable on the
        // explore polygon layer; a Point or null means "no shape to draw" — the
        // search point marker already sits at this location for feedback.
        const isDrawablePolygon = geometry
          && (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon');
        if (isDrawablePolygon) {
          this.addPolygonFeature(loc.placematrix_id, geometry);
        } else {
          this.$bvToast.toast('No shape available for this place.', {
            title: 'Zoom',
            variant: 'info',
            solid: true,
          });
        }
      } catch (e) {
        if (token !== this.zoomRequestToken) return; // stale: silently discard
        // Surface a real resolution failure instead of silently doing nothing.
        this.$bvToast.toast("Couldn't load this place's shape.", {
          title: 'Error',
          variant: 'warning',
          solid: true,
        });
      }
    },
    // Inject a Polygon/MultiPolygon feature into locationsGeoJSON (keyed by
    // placematrix_id) and re-emit the map data so the shape renders. No-op when
    // a polygon feature for this place is already present.
    addPolygonFeature(placematrixId, geometry) {
      if (!geometry) return;
      if (!this.locationsGeoJSON || !Array.isArray(this.locationsGeoJSON.features)) return;

      const alreadyHasPolygon = this.locationsGeoJSON.features.some(
        (f) => f.properties.placematrix_id === placematrixId
          && f.geometry
          && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'),
      );
      if (alreadyHasPolygon) return;

      const loc = this.locations.find((item) => item.placematrix_id === placematrixId);
      const newFeature = {
        type: 'Feature',
        geometry,
        properties: {
          placematrix_id: placematrixId,
          name: loc ? this.locationName(loc) : '',
        },
      };

      // Reassign (not push) so Vue's deep watcher on the emitted prop reliably
      // fires. In-place array mutation through the deep-watch/prop chain is
      // unreliable in Vue 2 and left late-arriving polygons unrendered.
      this.locationsGeoJSON = {
        ...this.locationsGeoJSON,
        features: [...this.locationsGeoJSON.features, newFeature],
      };

      this.emitExploreData();
    },
    // Pure selection helper: of the CURRENT page's locations, return those whose
    // point sits inside `bounds` and that don't already have a polygon shape
    // (cache miss) and haven't already been requested for this result set.
    // Reads point coordinates from the same locationsGeoJSON Point features the
    // map renders (matched by placematrix_id), matching zoomTo's lookup.
    placesInViewMissingPolygon(bounds) {
      const features = this.locationsGeoJSON && Array.isArray(this.locationsGeoJSON.features)
        ? this.locationsGeoJSON.features
        : [];
      if (!features.length || !bounds) return [];

      // Index point coordinates by placematrix_id for O(1) lookup per location.
      const pointByPlacematrix = new Map();
      features.forEach((feature) => {
        if (feature.geometry && feature.geometry.type === 'Point') {
          pointByPlacematrix.set(feature.properties.placematrix_id, feature.geometry.coordinates);
        }
      });

      return this.locations.filter((loc) => {
        if (this.autoGeometryRequested.has(loc.near_poi_id)) return false;
        if (this.cachedPolygonFor(loc)) return false;
        const coords = pointByPlacematrix.get(loc.placematrix_id);
        if (!Array.isArray(coords) || coords.length !== 2) return false;
        return pointInBounds(coords[0], coords[1], bounds);
      });
    },
    // Viewport-driven auto-fill (draw-only): when zoomed in past the threshold,
    // batch-resolve polygon geometries for in-view, current-page places that are
    // missing a shape and inject them onto the map. Never changes selection.
    async autoFillInViewGeometries(bounds, zoom) {
      if (zoom < AUTO_GEOMETRY_ZOOM_THRESHOLD) return;
      if (this.autoGeometryResolving) return;

      const misses = this.placesInViewMissingPolygon(bounds);
      if (!misses.length) return;

      // Mark as requested up front so a follow-up viewport change (e.g. a small
      // pan) doesn't re-submit these while the batch is in flight or after it
      // resolves to no shape.
      misses.forEach((loc) => this.autoGeometryRequested.add(loc.near_poi_id));

      // Guard against a result-set change (loadResults) landing a stale batch
      // onto the new list. resolveBulkGeometry never throws, but keep the
      // try/finally to always release the concurrency guard.
      const token = this.zoomRequestToken;
      this.autoGeometryResolving = true;
      try {
        const { results } = await this.resolveBulkGeometry(
          misses.map((l) => ({ near_poi_id: l.near_poi_id, lat: l.lat, lon: l.lon })),
          {
            custom: this.sourceFilter === 'Custom',
            companyId: this.selectedCompanyId,
            country: 'USA',
            // Interactive timeout so a background auto-fill can't hang for the
            // full 30s bulk default; matches the single-zoom path.
            timeout: 15000,
          },
        );

        if (token !== this.zoomRequestToken) return; // stale result set

        misses.forEach((loc) => {
          const geometry = results && results.get(loc.near_poi_id);
          const isDrawablePolygon = geometry
            && (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon');
          if (isDrawablePolygon) {
            // addPolygonFeature dedupes and re-emits the map data itself.
            this.addPolygonFeature(loc.placematrix_id, geometry);
          }
        });
      } catch (e) {
        // Auto-fill is a passive enhancement, not a user action — fail silently
        // (the point markers already sit at these locations for feedback).
      } finally {
        this.autoGeometryResolving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.place-selection-select-tab {
  height: 100%;
}

.left {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.filter-item {
  flex: 1;
  min-width: 0;
}

.results {
  // Absorb the leftover space between the filter controls and the pinned
  // pagination / select-all / button footer; the inner list scrolls, not the page.
  flex: 1 1 auto;
  min-height: 0;
}

.loc-group {
  border-top: 1px solid #ddd;
  height: 100%;
  overflow-y: auto;
  border: none;
}

.loc-header {
  font-weight: 700;
  font-size: 16px;
  color: #000;
}

.loc-content {
  font-size: 12px;
  letter-spacing: 3%;
  vertical-align: middle;
  color: #1a1a2e;
}

.loc-footer {
  font-size: 12px;
  letter-spacing: 3%;
  vertical-align: middle;
  color: #1a1a2e;
}

.pagination-info {
  font-size: 13px;
  color: #12263f;
}

.option-type {
  font-size: 10px;
  font-style: italic;
  color: #8c8ca1;
}
</style>
